import { useState, useEffect } from 'react';
import { Settings, User, Bell, Shield, Key, Database, Globe, Save, RefreshCw, IndianRupee, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ChangePasswordModal from '@/components/shared/ChangePasswordModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const [activeSection, setActiveSection] = useState('estateManager');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const [settings, setSettings] = useState({
    account: {
      name: 'Admin User',
      email: 'admin@altslice.com',
      phone: '+91 98765 00000',
    },
    notifications: {
      emailAlerts: true,
      investmentUpdates: true,
      brokerActivity: true,
      dailyDigest: false,
    },
    // Platform-wide parameters (wired to backend)
    defaultEoiAmount: 25000,
    autoReminders: true,
    originationFee: 500000,
    setupFee: 500000,
    annualMgmtFeePct: 1.0,
    carryFeePct: 25,
    carryHurdleRate: 15,
    brokerShareOfCarry: 50,
    brokeragePercent: 1.5,
    sellerTargetIRR: 15,
    interestPenaltyRate: 24,
    minCapitalContribution: 1000000,
    defaultArbitrationSeat: 'New Delhi',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/settings`);
        if (res.ok) {
          const data = await res.json();
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (e) {
        console.error('Failed to load settings', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sections = [
    { id: 'estateManager', label: 'Estate Manager Fees', icon: IndianRupee },
    { id: 'platform', label: 'Platform Config', icon: Globe },
    { id: 'account', label: 'Account', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'integrations', label: 'Integrations', icon: Key },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const { account, notifications, ...platformSettings } = settings;
      const res = await fetch(`${API}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(platformSettings),
      });
      if (res.ok) {
        toast.success('Settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (e) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateNested = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'account':
        return (
          <div className="space-y-6">
            <h3 className="text-lg text-white font-light">Account Settings</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Full Name</label>
                <input type="text" value={settings.account.name} onChange={e => updateNested('account', 'name', e.target.value)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-white/30" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Email</label>
                <input type="email" value={settings.account.email} onChange={e => updateNested('account', 'email', e.target.value)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-white/30" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Phone</label>
                <input type="text" value={settings.account.phone} onChange={e => updateNested('account', 'phone', e.target.value)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-white/30" />
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="text-lg text-white font-light">Notification Preferences</h3>
            <div className="space-y-4">
              {Object.entries({ emailAlerts: 'Email Alerts for Critical Actions', investmentUpdates: 'Investment Status Updates', brokerActivity: 'Broker Activity Notifications', dailyDigest: 'Daily Summary Digest' }).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between p-4 border border-white/10 bg-white/5">
                  <span className="text-sm text-white">{label}</span>
                  <button onClick={() => updateNested('notifications', key, !settings.notifications[key])} className={`w-12 h-6 rounded-full transition-colors ${settings.notifications[key] ? 'bg-[#4ADE80]' : 'bg-white/20'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${settings.notifications[key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'platform':
        return (
          <div className="space-y-6">
            <h3 className="text-lg text-white font-light">Platform Configuration</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Default EOI Amount (INR)</label>
                <input type="number" value={settings.defaultEoiAmount} onChange={e => updateSetting('defaultEoiAmount', parseInt(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-eoi-amount" />
                <p className="text-xs text-gray-600 mt-1">Default EOI amount for new properties</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Brokerage Rate (%)</label>
                <input type="number" step="0.1" value={settings.brokeragePercent} onChange={e => updateSetting('brokeragePercent', parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-brokerage-percent" />
                <p className="text-xs text-gray-600 mt-1">Brokerage on secondary sale (% of unit valuation)</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Broker Share of Carry (%)</label>
                <input type="number" value={settings.brokerShareOfCarry} onChange={e => updateSetting('brokerShareOfCarry', parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-broker-share-carry" />
                <p className="text-xs text-gray-600 mt-1">Broker's share of platform carry on performance fees</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Seller Target IRR (% p.a.)</label>
                <input type="number" step="0.1" value={settings.sellerTargetIRR} onChange={e => updateSetting('sellerTargetIRR', parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-seller-target-irr" />
                <p className="text-xs text-gray-600 mt-1">Target IRR for sellers in secondary pricing model</p>
              </div>
              <div className="flex items-center gap-4">
                <label className="text-xs text-gray-500 uppercase">Auto Payment Reminders</label>
                <button onClick={() => updateSetting('autoReminders', !settings.autoReminders)} className={`w-12 h-6 rounded-full transition-colors ${settings.autoReminders ? 'bg-[#4ADE80]' : 'bg-white/20'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${settings.autoReminders ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          </div>
        );

      case 'estateManager':
        return (
          <div className="space-y-6" data-testid="estate-manager-settings">
            <div>
              <h3 className="text-lg text-white font-light">Estate Manager Fee Structure</h3>
              <p className="text-sm text-gray-500 mt-1">These values are used platform-wide for LLP agreements and secondary marketplace calculations.</p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">One-Time Origination Fee (INR)</label>
                <input type="number" value={settings.originationFee} onChange={e => updateSetting('originationFee', parseInt(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-origination-fee" />
                <p className="text-xs text-gray-600 mt-1">Charged per unit at origination</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">One-Time Setup Fee (INR)</label>
                <input type="number" value={settings.setupFee} onChange={e => updateSetting('setupFee', parseInt(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-setup-fee" />
                <p className="text-xs text-gray-600 mt-1">LLP setup and legal documentation</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Annual Asset Management Fee (%)</label>
                <input type="number" step="0.1" value={settings.annualMgmtFeePct} onChange={e => updateSetting('annualMgmtFeePct', parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-annual-mgmt-fee" />
                <p className="text-xs text-gray-600 mt-1">% of total asset value, charged annually</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Carry / Exit Management Fee (%)</label>
                <input type="number" value={settings.carryFeePct} onChange={e => updateSetting('carryFeePct', parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-carry-fee" />
                <p className="text-xs text-gray-600 mt-1">% of returns above hurdle rate, charged at exit</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Carry Hurdle Rate (% p.a.)</label>
                <input type="number" value={settings.carryHurdleRate} onChange={e => updateSetting('carryHurdleRate', parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-hurdle-rate" />
                <p className="text-xs text-gray-600 mt-1">Carry fee applies only on returns exceeding this rate</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Interest Penalty Rate (% p.a.)</label>
                <input type="number" value={settings.interestPenaltyRate} onChange={e => updateSetting('interestPenaltyRate', parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-penalty-rate" />
                <p className="text-xs text-gray-600 mt-1">Penalty on delayed capital contributions</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Default Minimum Capital (INR)</label>
                <input type="number" value={settings.minCapitalContribution} onChange={e => updateSetting('minCapitalContribution', parseInt(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-white/30" data-testid="input-min-capital" />
                <p className="text-xs text-gray-600 mt-1">Platform-wide default. Can be overridden per unit.</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-2 block">Default Arbitration Seat</label>
                <input type="text" value={settings.defaultArbitrationSeat} onChange={e => updateSetting('defaultArbitrationSeat', e.target.value)} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-white/30" data-testid="input-arbitration-seat" />
                <p className="text-xs text-gray-600 mt-1">Jurisdiction for dispute resolution</p>
              </div>
            </div>
            <div className="p-4 border border-white/10 bg-white/[0.03] mt-4">
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-3">Fee Summary Preview</h4>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Origination</p>
                  <p className="text-white font-mono">{(settings.originationFee / 100000).toFixed(1)}L</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Setup</p>
                  <p className="text-white font-mono">{(settings.setupFee / 100000).toFixed(1)}L</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Annual Mgmt</p>
                  <p className="text-white font-mono">{settings.annualMgmtFeePct}%</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Carry</p>
                  <p className="text-white font-mono">{settings.carryFeePct}% above {settings.carryHurdleRate}%</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <h3 className="text-lg text-white font-light">Security Settings</h3>
            <div className="space-y-4">
              <div className="p-4 border border-white/10 bg-white/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white">Two-Factor Authentication</span>
                  <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400">Not Enabled</span>
                </div>
                <p className="text-xs text-gray-500">Add an extra layer of security to your account</p>
              </div>
              <div className="p-4 border border-white/10 bg-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-white">Change Password</span>
                    <p className="text-xs text-gray-500 mt-1">Update your admin account password</p>
                  </div>
                  <button onClick={() => setShowChangePassword(true)} className="px-4 py-2 text-sm border border-white/20 text-white hover:bg-white/10 transition-colors" data-testid="admin-change-pw-btn">
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'integrations':
        return (
          <div className="space-y-6">
            <h3 className="text-lg text-white font-light">Integrations</h3>
            <div className="space-y-4">
              {[
                { name: 'Zoho Sign', color: 'blue', status: 'Connected' },
                { name: 'Zoho Flow', color: 'purple', status: 'Connected' },
                { name: 'Payment Gateway', color: 'gray', status: 'Not Configured' },
              ].map(({ name, color, status }) => (
                <div key={name} className="p-4 border border-white/10 bg-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 bg-${color}-500/20 flex items-center justify-center`}>
                        <span className={`text-${color}-400 text-xs font-bold`}>{name[0]}</span>
                      </div>
                      <span className="text-sm text-white">{name}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 ${status === 'Connected' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}>{status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-8" data-testid="admin-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Settings className="w-5 h-5 text-[#4ADE80]" />
            <h1 className="text-2xl font-light text-white">Settings</h1>
          </div>
          <p className="text-sm text-gray-500">Manage your account and platform configuration</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-settings-btn" className="flex items-center gap-2 px-6 py-3 bg-[#4ADE80] text-black font-medium hover:bg-[#4ADE80]/90 disabled:opacity-50">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>

      <div className="flex gap-8">
        <div className="w-64 flex-shrink-0">
          <div className="space-y-1">
            {sections.map(section => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button key={section.id} onClick={() => setActiveSection(section.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isActive ? 'bg-white/10 border-l-2 border-[#4ADE80] text-white' : 'text-gray-500 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 border border-white/10 bg-white/[0.02] p-8">
          {renderSection()}
        </div>
      </div>
      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
}
